import { Injectable, Inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';

import { DOCUMENT } from '@angular/common';
import { Language } from '../models/language.model';
import { User } from '../models/identity/user.model';
import { ProcessTypeFeatureFlags } from '../models/process-type-feature-flags.enum';

@Injectable()
export class ConfigService {
  private configVar: any;
  private user: User;
  private languages: Array<Language>;

  constructor(
    private readonly http: HttpClient,
    @Inject(DOCUMENT) private readonly document: Document
  ) {}

  get configData(): any {
    return this.configVar;
  }

  get apiUrl(): string {
    return this.document.location.protocol + '//' + this.document.location.host;
  }

  get getLanguages(): Array<Language> {
    return this.languages;
  }

  isFeatureEnabled(feature: ProcessTypeFeatureFlags) {
    return this.configVar.processTypeFeatureFlags[
      ProcessTypeFeatureFlags[feature]
    ];
  }

  getAllLanguages(): Promise<any> {
    if (!this.apiUrl) {
      return Promise.resolve();
    }
    return this.http
      .get(this.apiUrl + '/api/language/languages', this.getRequestOptions())
      .toPromise()
      .then((languages: Array<Language>) => {
        this.languages = languages;
      })
      .catch((err: any) => Promise.resolve());
  }

  async getAllLanguagesAsync(): Promise<Array<Language>> {
    return this.http
    .get<Array<Language>>(this.apiUrl + '/api/language/languages', this.getRequestOptions())
    .toPromise() as Promise<Array<Language>>
  }

  getConfigVar(): Promise<any> {
    this.configVar = null;
    return this.http
      .get('./assets/config/config.json')
      .toPromise()
      .then((data: any) => (this.configVar = data))
      .catch((err: any) => Promise.resolve());
  }

  getUserPicture(): Promise<any> {
    if (!this.apiUrl) {
      return Promise.resolve();
    }
    this.http
      .get(
        this.apiUrl + `/api/user/picture/${this.user.id}`,
        this.getImageRequestOptions()
      )
      .toPromise()
      .then((imageBlob: Blob) => {
        this.createImageFromBlob(imageBlob, this.user);
      })
      .catch((err: any) => Promise.resolve());
  }

  public createImageFromBlob(imageBlob: Blob, source) {
    const reader = new FileReader();
    reader.addEventListener(
      'load',
      () => {
        source.employeePhoto = reader.result;
      },
      false
    );

    if (imageBlob) {
      reader.readAsDataURL(imageBlob);
    }
  }

  public getUtcDate(date: string = '') {
    const newDate = date.length > 1 ? new Date(date) : new Date();
    const year = newDate.getUTCFullYear();
    const month = newDate.getUTCMonth();
    const day = newDate.getUTCDate();
    const hours = newDate.getUTCHours();
    const minutes = newDate.getUTCMinutes();
    const seconds = newDate.getUTCSeconds();
    return new Date(year, month, day, hours, minutes, seconds);
  }

  public appendHttpParams(
    params: HttpParams,
    name: string,
    values: Array<number>
  ) {
    if (values && values.length !== 0) {
      values.forEach((id) => (params = params.append(name, id.toString())));
    }
    return params;
  }

  public getImageRequestOptions(): Object {
    const options = {
      headers: new HttpHeaders({
        Accept: 'image/jpeg',
        'Content-Type': 'image/jpeg',
      }),
      responseType: 'blob',
    };
    return options;
  }

  protected getRequestOptions() {
    const requestOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
      }),
    };
    return requestOptions;
  }
}
